import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RefreshCw, Trash2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { usePWA } from "./PWAInstaller";

export function CacheManager() {
  const { token } = useAuth();
  const { checkForUpdates, clearCache, updateAvailable } = usePWA();
  const [isChecking, setIsChecking] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [lastCheck, setLastCheck] = useState<string | null>(null);

  const handleCheckForUpdates = async () => {
    setIsChecking(true);
    try {
      await checkForUpdates();
      setLastCheck(new Date().toLocaleString());
    } catch (error) {
      console.error("Update check failed:", error);
    } finally {
      setIsChecking(false);
    }
  };

  const handleClearCache = async () => {
    if (
      !confirm("This will clear all cached data and reload the page. Continue?")
    ) {
      return;
    }

    setIsClearing(true);
    try {
      await clearCache();
    } catch (error) {
      console.error("Cache clear failed:", error);
      setIsClearing(false);
    }
  };

  const handleServerCacheClear = async () => {
    if (
      !confirm("This will request server-side cache invalidation. Continue?")
    ) {
      return;
    }

    try {
      const response = await fetch("/api/cache/clear", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        alert(
          "Cache invalidation requested. The app will check for updates on next page load."
        );
      } else {
        alert("Failed to request cache invalidation.");
      }
    } catch (error) {
      console.error("Server cache clear failed:", error);
      alert("Failed to request cache invalidation.");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5" />
          Cache Management
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-muted-foreground">
          Manage PWA cache and check for app updates.
        </div>

        {updateAvailable && (
          <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
            <span className="text-sm text-yellow-800 dark:text-yellow-200">
              A new version is available. Refresh the page to update.
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Button
            onClick={handleCheckForUpdates}
            disabled={isChecking}
            variant="outline"
            className="h-auto p-3 flex flex-col items-center gap-2"
          >
            <RefreshCw
              className={`h-4 w-4 ${isChecking ? "animate-spin" : ""}`}
            />
            <div className="text-center">
              <div className="font-medium">Check for Updates</div>
              <div className="text-xs text-muted-foreground">
                {isChecking ? "Checking..." : "Check for new app version"}
              </div>
            </div>
          </Button>

          <Button
            onClick={handleClearCache}
            disabled={isClearing}
            variant="outline"
            className="h-auto p-3 flex flex-col items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            <div className="text-center">
              <div className="font-medium">Clear All Cache</div>
              <div className="text-xs text-muted-foreground">
                {isClearing ? "Clearing..." : "Clear cached data and reload"}
              </div>
            </div>
          </Button>
        </div>

        <div className="pt-2 border-t">
          <Button
            onClick={handleServerCacheClear}
            variant="ghost"
            size="sm"
            className="text-xs"
          >
            Request Server Cache Invalidation
          </Button>
        </div>

        {lastCheck && (
          <div className="text-xs text-muted-foreground">
            Last checked: {lastCheck}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
